import {
  Document,
  Link,
  Page,
  Path,
  pdf,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import { useEffect, useState, type ReactElement } from "react";
import QRCode from "react-qr-code";

import { DocumentTemplateLayoutEnum } from "@/__generated__/graphql";
import { TagColors } from "@/constants/values";
import {
  INVOICE_LABELS,
  type InvoiceTemplate,
  TEMPLATE_FONTS,
} from "./DocumentPreview.content";
import { type InvoiceDescription } from "./DocumentPreview.utils";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#1a1a1a",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    letterSpacing: -0.5,
  },
  metaContainer: {
    flexDirection: "row",
    gap: 30,
  },
  metaBlock: {
    textAlign: "left",
  },
  metaLabel: {
    fontSize: 9,
    color: "#666",
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 10,
    color: "#1a1a1a",
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    marginVertical: 15,
  },
  addressRow: {
    flexDirection: "row",
    marginBottom: 25,
  },
  addressBlock: {
    width: "50%",
  },
  addressLabel: {
    fontSize: 9,
    color: "#666",
    marginBottom: 6,
  },
  addressText: {
    fontSize: 10,
    color: "#1a1a1a",
    marginBottom: 2,
  },
  amountDueHeadline: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
    letterSpacing: -0.3,
  },
  description: {
    fontSize: 10,
    color: "#444",
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#1a1a1a",
    paddingBottom: 8,
    marginBottom: 0,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#1a1a1a",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingVertical: 10,
  },
  tableCell: {
    fontSize: 10,
  },
  col1: { width: "35%" },
  col2: { width: "12%", textAlign: "right" },
  col3: { width: "17%", textAlign: "right" },
  col4: { width: "19%", textAlign: "right" },
  col5: { width: "17%", textAlign: "right" },
  totalsSection: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  totalsBox: {
    width: 200,
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 5,
  },
  totalsLabel: {
    fontSize: 10,
    color: "#666",
  },
  totalsValue: {
    fontSize: 10,
    color: "#1a1a1a",
  },
  totalsDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    marginVertical: 5,
  },
  totalsFinalLabel: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#1a1a1a",
  },
  totalsFinalValue: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#1a1a1a",
  },
  waysToPay: {
    marginTop: 25,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
  waysToPayTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 8,
  },
  waysToPayRow: {
    flexDirection: "row",
    gap: 30,
  },
  waysToPayLabel: {
    fontSize: 9,
    fontWeight: "bold",
    marginTop: 6,
    marginBottom: 2,
  },
  footer: {
    marginTop: 25,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    fontSize: 9,
    color: "#666",
  },
});

const COLUMN_STYLES = [
  styles.col1,
  styles.col2,
  styles.col3,
  styles.col4,
  styles.col5,
];

/**
 * The invoice as its PDF: what it says, as ``describeInvoice`` words it for
 * its canvas too, drawn in its *template*'s look.
 */
function InvoicePDF({
  invoice,
  template,
}: {
  invoice: InvoiceDescription;
  template?: InvoiceTemplate | null;
}) {
  // Its template's look, as on screen: a band of its colour behind its
  // heading, or the same closer together; its colour; its typeface - one of
  // the three a PDF has. One with no template prints as an invoice always has.
  const isBanner = template?.layout === DocumentTemplateLayoutEnum.Banner;
  const isCompact = template?.layout === DocumentTemplateLayoutEnum.Compact;
  const accent = template?.color
    ? TagColors[template.color].solidColor
    : undefined;
  const typeface = template?.font ? TEMPLATE_FONTS[template.font] : undefined;
  const bold = typeface ? { fontFamily: typeface.pdfBold } : {};
  const onBand = isBanner ? { color: "#ffffff" } : {};
  // The QR code the canvas draws for the page it is paid at, cell for cell:
  // the code's own element, whose path is its dark cells.
  const qr = invoice.card
    ? ((
        QRCode as unknown as {
          render: (
            props: { value: string },
            ref: null,
          ) => ReactElement<{
            fgD: string;
            viewBoxSize: number;
          }>;
        }
      ).render({ value: invoice.card.url }, null).props as {
        fgD: string;
        viewBoxSize: number;
      })
    : null;

  return (
    <Document>
      <Page
        size="A4"
        style={[
          styles.page,
          typeface ? { fontFamily: typeface.pdf } : {},
          isCompact ? { padding: 28, fontSize: 9 } : {},
        ]}
      >
        {/* Header - on a band of its colour, or over a rule */}
        <View
          style={[
            styles.header,
            isBanner
              ? {
                  backgroundColor: accent,
                  marginHorizontal: -40,
                  marginTop: -40,
                  paddingHorizontal: 40,
                  paddingTop: 30,
                  paddingBottom: 20,
                }
              : {},
          ]}
        >
          <Text
            style={[
              styles.title,
              bold,
              accent ? { color: accent } : {},
              isCompact ? { fontSize: 20 } : {},
              onBand,
            ]}
          >
            {INVOICE_LABELS.title}
          </Text>
          <View style={styles.metaContainer}>
            <View style={styles.metaBlock}>
              <Text style={[styles.metaLabel, onBand]}>
                {INVOICE_LABELS.number}
              </Text>
              <Text style={[styles.metaValue, onBand]}>{invoice.number}</Text>
            </View>
            <View style={styles.metaBlock}>
              <Text style={[styles.metaLabel, onBand]}>
                {INVOICE_LABELS.issued}
              </Text>
              <Text style={[styles.metaValue, onBand]}>{invoice.issued}</Text>
            </View>
          </View>
        </View>

        {!isBanner && (
          <View
            style={[
              styles.divider,
              accent ? { borderBottomColor: accent } : {},
            ]}
          />
        )}

        {/* Billed To / Issued By */}
        <View style={styles.addressRow}>
          {(
            [
              [INVOICE_LABELS.billedTo, invoice.billedTo],
              [INVOICE_LABELS.issuedBy, invoice.issuedBy],
            ] as const
          ).map(([label, lines]) => (
            <View key={label} style={styles.addressBlock}>
              <Text style={styles.addressLabel}>{label}</Text>
              {lines.map((line, index) => (
                <Text key={index} style={styles.addressText}>
                  {line}
                </Text>
              ))}
            </View>
          ))}
        </View>

        {/* Amount Due Headline */}
        <Text
          style={[
            styles.amountDueHeadline,
            bold,
            accent ? { color: accent } : {},
            isCompact ? { fontSize: 14 } : {},
          ]}
        >
          {invoice.headline}
        </Text>

        {invoice.notes && (
          <Text style={styles.description}>{invoice.notes}</Text>
        )}

        {/* Line Items Table */}
        <View
          style={[
            styles.tableHeader,
            accent ? { borderBottomColor: accent } : {},
          ]}
        >
          {INVOICE_LABELS.columns.map((label, index) => (
            <Text
              key={label}
              style={[styles.tableHeaderText, bold, COLUMN_STYLES[index]]}
            >
              {label}
            </Text>
          ))}
        </View>

        {invoice.lines.map((line, index) => (
          <View
            key={index}
            style={[styles.tableRow, isCompact ? { paddingVertical: 6 } : {}]}
          >
            <View style={styles.col1}>
              {line.description.split("\n").map((text, row) => (
                <Text key={row} style={styles.tableCell}>
                  {text}
                </Text>
              ))}
            </View>
            {[line.quantity, line.unitPrice, line.tax, line.total].map(
              (text, column) => (
                <Text
                  key={column}
                  style={[styles.tableCell, COLUMN_STYLES[column + 1]]}
                >
                  {text}
                </Text>
              ),
            )}
          </View>
        ))}

        {/* Totals: the last, what is due, set apart in bold. */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsBox}>
            {invoice.totals.map(({ label, value }, index) => {
              const isDue = index === invoice.totals.length - 1;
              return (
                <View key={label}>
                  {isDue && <View style={styles.totalsDivider} />}
                  <View style={styles.totalsRow}>
                    <Text
                      style={
                        isDue
                          ? [styles.totalsFinalLabel, bold]
                          : styles.totalsLabel
                      }
                    >
                      {label}
                    </Text>
                    <Text
                      style={
                        isDue
                          ? [
                              styles.totalsFinalValue,
                              bold,
                              accent ? { color: accent } : {},
                            ]
                          : styles.totalsValue
                      }
                    >
                      {value}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>

        {/* Ways to pay: the ones the invoice takes. */}
        {(invoice.eTransfer || invoice.card) && (
          <View style={styles.waysToPay}>
            <Text style={[styles.waysToPayTitle, bold]}>
              {INVOICE_LABELS.waysToPay}
            </Text>
            <View style={styles.waysToPayRow}>
              {invoice.eTransfer && (
                <View style={{ flex: 1 }}>
                  <Text style={[styles.waysToPayLabel, bold]}>
                    {INVOICE_LABELS.eTransfer}
                  </Text>
                  <Text
                    style={invoice.eTransfer.isGiven ? {} : { color: "#666" }}
                  >
                    {invoice.eTransfer.instructions}
                  </Text>
                  {invoice.eTransfer.reference && (
                    <Text style={{ color: "#666", marginTop: 2 }}>
                      {invoice.eTransfer.reference}
                    </Text>
                  )}
                </View>
              )}
              {invoice.card && qr && (
                <View>
                  <Link
                    src={invoice.card.url}
                    style={[styles.waysToPayLabel, bold, { color: "#1a1a1a" }]}
                  >
                    {INVOICE_LABELS.card}
                  </Link>
                  <Text style={{ color: "#666", marginBottom: 6 }}>
                    {INVOICE_LABELS.scan}
                  </Text>
                  <Svg
                    viewBox={`0 0 ${qr.viewBoxSize} ${qr.viewBoxSize}`}
                    style={{ width: 72, height: 72 }}
                  >
                    <Path d={qr.fgD} fill="#1a1a1a" />
                  </Svg>
                </View>
              )}
            </View>
          </View>
        )}

        {invoice.footer && (
          <Text
            style={[styles.footer, accent ? { borderTopColor: accent } : {}]}
          >
            {invoice.footer}
          </Text>
        )}
      </Page>
    </Document>
  );
}

/**
 * The PDF of *invoice* - one file, which a page shows and hands to whoever
 * downloads it, so the two are the same bytes - and where it is read from.
 * None while it is drawn: drawn again as what it says changes.
 */
export function useInvoicePdf(
  invoice: InvoiceDescription | null,
  template: InvoiceTemplate | null | undefined,
) {
  const [file, setFile] = useState<{ blob: Blob; url: string } | null>(null);
  useEffect(() => {
    setFile(null);
    if (!invoice) return;
    let isCurrent = true;
    let url: string | null = null;
    void pdf(<InvoicePDF invoice={invoice} template={template} />)
      .toBlob()
      .then((blob) => {
        if (!isCurrent) return;
        url = URL.createObjectURL(blob);
        setFile({ blob, url });
      });
    return () => {
      isCurrent = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [invoice, template]);
  return file;
}
